/* iter-40 — Drag-and-drop ebook uploader for the Bookstore admin.
 *
 * Reads the existing /admin/bookstore/books/{id}/file metadata,
 * lets the admin drag a .md/.txt/.epub onto the dropzone (or click
 * to browse), and POSTs as multipart to the same endpoint. Shows
 * file metadata + manifest_version once stored, and a Delete button
 * to wipe the file (the /content endpoint then falls back to the
 * synthetic stub).
 *
 * Used inside the Books-list panel — opened from a per-row "Manuscript"
 * button next to Edit.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Upload, FileText, Trash2, X, BookOpen, CheckCircle2 } from 'lucide-react';
import { bookstoreAdminApi } from '@/lib/bookstoreApi';

const ACCEPT = '.md,.markdown,.txt,.epub';
const MAX_MB = 25;

function humanBytes(n) {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export default function BookFileUploadPanel({ book, onClose }) {
  const [meta, setMeta] = useState(null);   // null = loading, false = no file, obj = file row
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const reload = useCallback(async () => {
    try {
      const r = await bookstoreAdminApi.getBookFile(book.id);
      setMeta(r.file || false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load file metadata.');
      setMeta(false);
    }
  }, [book.id]);

  useEffect(() => { reload(); }, [reload]);

  const upload = async (file) => {
    if (!file) return;
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!['md', 'markdown', 'txt', 'epub'].includes(ext)) {
      toast.error('Use .md, .txt or .epub.');
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File exceeds ${MAX_MB} MB.`);
      return;
    }
    setBusy(true);
    try {
      const r = await bookstoreAdminApi.uploadBookFile(book.id, file);
      setMeta(r.file);
      toast.success(`Stored ${r.file.chapter_count} chapter${r.file.chapter_count === 1 ? '' : 's'} · v${r.file.manifest_version}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) upload(f);
  };

  const remove = async () => {
    if (!window.confirm('Remove the stored manuscript? The bookstore reader will fall back to the synthetic stub.')) return;
    setBusy(true);
    try {
      await bookstoreAdminApi.deleteBookFile(book.id);
      setMeta(false);
      toast.success('Manuscript removed.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
          data-testid="bs-book-file-modal">
      <div className="w-full max-w-xl overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border bg-panel-header/50 px-5 py-3">
          <div className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-primary" />
            <div className="text-[13px] font-semibold">Manuscript · {book.title}</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="bs-book-file-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          {meta === null && (
            <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}

          {meta && meta !== false && (
            <div className="rounded-md border border-success/40 bg-success/5 p-4"
                  data-testid="bs-book-file-meta">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-4 w-4 text-success" />
                <div className="flex-1 min-w-0">
                  <div className="truncate text-[13px] font-semibold">{meta.filename}</div>
                  <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-[11.5px] text-muted-foreground">
                    <div>Size: <span className="text-foreground">{humanBytes(meta.byte_size)}</span></div>
                    <div>Format: <span className="text-foreground">.{meta.format}</span></div>
                    <div>Chapters: <span className="text-foreground">{meta.chapter_count}</span></div>
                    <div>Words: <span className="text-foreground">{(meta.total_words || 0).toLocaleString()}</span></div>
                    <div className="col-span-2">Manifest version: <span className="text-foreground">v{meta.manifest_version}</span></div>
                    <div className="col-span-2">Uploaded: <span className="text-foreground">{new Date(meta.uploaded_at).toLocaleString()}</span></div>
                  </div>
                </div>
                <button onClick={remove} disabled={busy}
                         className="rounded p-1.5 text-warning hover:bg-warning/10 disabled:opacity-50"
                         title="Remove manuscript" data-testid="bs-book-file-delete">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}

          {meta === false && (
            <div className="rounded-md border border-dashed border-muted/50 bg-muted/10 p-3 text-[11.5px] text-muted-foreground">
              No manuscript stored yet. Customers will see the synthetic stub when reading this book.
            </div>
          )}

          {/* Dropzone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-7 text-center transition
              ${dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/60 hover:bg-secondary/20'}
              ${busy ? 'pointer-events-none opacity-50' : ''}`}
            data-testid="bs-book-file-dropzone">
            {busy ? (
              <>
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <div className="text-[12px] text-muted-foreground">Uploading…</div>
              </>
            ) : (
              <>
                <Upload className="h-6 w-6 text-primary" />
                <div className="text-[13px] font-semibold">
                  {meta ? 'Replace manuscript' : 'Drop a manuscript here'}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  .md / .txt / .epub · up to {MAX_MB} MB · or click to browse
                </div>
              </>
            )}
            <input ref={inputRef} type="file" accept={ACCEPT} className="hidden"
                    onChange={(e) => upload(e.target.files?.[0])}
                    data-testid="bs-book-file-input" />
          </div>

          <div className="rounded-md border border-border/60 bg-secondary/20 p-3 text-[11px] text-muted-foreground">
            <div className="mb-1 flex items-center gap-1.5 font-semibold text-foreground">
              <FileText className="h-3 w-3" /> Markdown format
            </div>
            Separate chapters with <code className="rounded bg-background px-1">## Chapter N — Title</code> headings.
            The reader picks each chapter up automatically and bumps the manifest version every time you re-upload.
            EPUB files are stored verbatim and reserved for future readers.
          </div>
        </div>
      </div>
    </div>
  );
}
